import { useState, useEffect } from 'preact/hooks';
import { integrationsApi } from '@/api/integrations';
import { IntegrationProvider, IntegrationConnectionStatus, INTEGRATION_PROVIDER_LABELS } from '@/types/integrations';
const BIGMARKER_FALLBACK_LABEL = INTEGRATION_PROVIDER_LABELS[IntegrationProvider.BigMarker] || 'BigMarker';
export function useBigMarkerIntegration(clientCompanyId) {
    const [hasBigMarker, setHasBigMarker] = useState(false);
    const [integrationError, setIntegrationError] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);
    const [integrationName, setIntegrationName] = useState(BIGMARKER_FALLBACK_LABEL);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let mounted = true;
        // Reset to loading state when companyId changes to avoid stale flags
        setLoading(true);
        setHasBigMarker(false);
        setIntegrationError(false);
        setStatusMessage(null);
        setIntegrationName(BIGMARKER_FALLBACK_LABEL);
        if (!clientCompanyId) {
            setLoading(false);
            return;
        }
        async function check() {
            try {
                const res = await integrationsApi.getIntegrationConnections(clientCompanyId);
                if (!mounted)
                    return;
                if (!res.data) {
                    setHasBigMarker(false);
                    setIntegrationError(false);
                    setStatusMessage(null);
                    return;
                }
                const bmConnection = res.data.find(c => c.Provider === IntegrationProvider.BigMarker);
                if (!bmConnection) {
                    setHasBigMarker(false);
                    setIntegrationError(false);
                    setStatusMessage(null);
                    return;
                }
                // Connection exists — hasBigMarker is true regardless of health
                setHasBigMarker(true);
                const isError = bmConnection.Status === IntegrationConnectionStatus.Error
                    || bmConnection.Status === IntegrationConnectionStatus.AuthExpired;
                setIntegrationError(isError);
                setStatusMessage(isError ? (bmConnection.StatusMessage || null) : null);
                const providerLabel = INTEGRATION_PROVIDER_LABELS[bmConnection.Provider];
                setIntegrationName(bmConnection.Name?.trim() || providerLabel || BIGMARKER_FALLBACK_LABEL);
            }
            catch (error) {
                console.warn('Failed to fetch BigMarker integration status:', error);
            }
            finally {
                if (mounted)
                    setLoading(false);
            }
        }
        check();
        return () => { mounted = false; };
    }, [clientCompanyId]);
    return { hasBigMarker, integrationError, integrationName, statusMessage, loading };
}
